import { axiosInstance } from "../../../api/axiosInstance";
import { ClassTimetable, FacultyTimetable, Period, TimetableSlot } from "../types";

/**
 * Timetables in the active academic year (Academics → Timetable, My Timetable). See docs/timetable.md.
 *
 *   GET /timetable/classes/{gradeSectionId}                     → ClassTimetable          (admin)
 *   PUT /timetable/classes/{gradeSectionId}  { slots: [...] }   → ClassTimetable          (admin)
 *   GET /timetable/faculties/{facultyId}                        → FacultyTimetable        (admin)
 *   GET /timetable/me                                           → FacultyTimetable        (faculty)
 *
 * PUT replaces the class's whole week in one transaction (all or nothing). It fails with 409
 * (plain-text reason listing every clash) when a faculty is already in another class then, and with
 * 400 when the grade has no such period that day, it's a break, or an assignment isn't this class's.
 */

// The backend drops empty lists, nulls and false from JSON (jackson non_empty) — put them back.
const withBreak = (periods?: Period[]): Period[] => (periods ?? []).map((p) => ({ ...p, isBreak: Boolean(p.isBreak) }));

const normalize = (data: ClassTimetable): ClassTimetable => ({
  ...data,
  academicYear: data.academicYear ?? null,
  periods: withBreak(data.periods),
  days: data.days ?? {},
  assignments: data.assignments ?? [],
  slots: data.slots ?? [],
  busy: data.busy ?? [],
});

const normalizeWeek = (data: FacultyTimetable): FacultyTimetable => ({
  ...data,
  academicYear: data.academicYear ?? null,
  periods: withBreak(data.periods),
  days: data.days ?? [],
  slots: data.slots ?? [],
});

export const fetchClassTimetable = async (gradeSectionId: number): Promise<ClassTimetable> => {
  const response = await axiosInstance.get<ClassTimetable>(`/timetable/classes/${gradeSectionId}`);
  return normalize(response.data);
};

/** Replaces the class's whole week with `slots` (all or nothing); returns the timetable afterwards. */
export const saveClassTimetable = async (gradeSectionId: number, slots: TimetableSlot[]): Promise<ClassTimetable> => {
  const response = await axiosInstance.put<ClassTimetable>(`/timetable/classes/${gradeSectionId}`, { slots });
  return normalize(response.data);
};

/** Admin: any faculty's week (read-only). */
export const fetchFacultyTimetable = async (facultyId: number): Promise<FacultyTimetable> => {
  const response = await axiosInstance.get<FacultyTimetable>(`/timetable/faculties/${facultyId}`);
  return normalizeWeek(response.data);
};

/** The logged-in faculty's own week (My Timetable). */
export const fetchMyTimetable = async (): Promise<FacultyTimetable> => {
  const response = await axiosInstance.get<FacultyTimetable>("/timetable/me");
  return normalizeWeek(response.data);
};
